import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { translate } from 'react-i18next'
import ContentHeader from 'component/utils/ContentHeader'
import NewPagination from 'component/pagination/newPagination'
import Row from 'component/utils/Row'
import Col from 'component/utils/Col'
import SizeSelect, { components } from 'react-select'
import Modal from 'component/Modal'
import Button from 'component/Button'
import SuccessModal from 'component/Modal/SuccessModal'
import MagnifierAlt from 'images/magnifier-alt.svg'
import ClearSVG from 'component/svgComponents/clear'
import DropdownArrow from 'component/svgComponents/dropdownArrow'
import { colourStyles } from 'utils/selectStyle'
import * as externalApisActions from 'action/externalApis'
import Select from '../../components/Select/Select'
import ExternalApisTable from './ExternalApisTable'
import ExternalApiEditModal from './ExternalApiEditModal'
import './styles.css'

const TARGET_FILTER_OPTIONS = [
  { label: 'DEV', value: 'DEV' },
  { label: 'PRD', value: 'PRD' },
]

const PAGE_SIZE_OPTIONS = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
  { label: '40', value: 40 },
]

class ExternalApisManagement extends Component {
  static propTypes = {
    translate: PropTypes.func.isRequired,
    auth: PropTypes.object.isRequired,
    records: PropTypes.array.isRequired,
    count: PropTypes.number,
    pages: PropTypes.number,
    current: PropTypes.number,
    filter: PropTypes.object,
    isUpdating: PropTypes.bool,
    togglingIds: PropTypes.array,
    fetchExternalApis: PropTypes.func.isRequired,
    updateExternalApi: PropTypes.func.isRequired,
    toggleExternalApiTarget: PropTypes.func.isRequired,
    resetExternalApis: PropTypes.func.isRequired,
  }

  state = {
    searchText: '',
    optionSize: 10,
    pagination: { page: 0, size: 10 },
    selectedRecord: null,
    modalMode: 'view',
    pendingToggleRecord: null,
    successMessage: '',
    targetFilter: [],
  }

  componentDidMount() {
    this.handleFetchExternalApis(0)
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.current !== this.props.current &&
      this.props.current > 0 &&
      this.props.current - 1 !== this.state.pagination.page
    ) {
      this.setState(({ pagination }) => ({
        pagination: { ...pagination, page: this.props.current - 1 },
      }))
    }
  }

  componentWillUnmount() {
    this.props.resetExternalApis()
  }

  handleFetchExternalApis = (page = this.state.pagination.page) => {
    const {
      fetchExternalApis,
      auth: { user },
      filter,
    } = this.props
    const { optionSize, searchText, targetFilter } = this.state

    const activeTargets = (targetFilter || []).map((opt) => opt.value)

    const { activeTarget: _ignored, ...restFilter } = filter || {}

    const payloadFilter = {
      ...restFilter,
      search: searchText.trim(),
      size: optionSize,
      ...(activeTargets.length > 0 ? { activeTarget: activeTargets } : {}),
    }

    fetchExternalApis(user.dsToken, payloadFilter, page + 1)
  }

  handleSearchSubmit = (event) => {
    event.preventDefault()
    this.setState(
      ({ pagination }) => ({
        pagination: { ...pagination, page: 0 },
      }),
      () => this.handleFetchExternalApis(0)
    )
  }

  handleTargetFilterChange = (selectedOptions) => {
    this.setState(
      ({ pagination }) => ({
        targetFilter: selectedOptions || [],
        pagination: { ...pagination, page: 0 },
      }),
      () => this.handleFetchExternalApis(0)
    )
  }

  handleClearSearch = () => {
    this.setState(
      ({ pagination }) => ({
        searchText: '',
        pagination: { ...pagination, page: 0 },
      }),
      () => this.handleFetchExternalApis(0)
    )
  }

  handleToggleTarget = (record) => {
    this.setState({ pendingToggleRecord: record })
  }

  confirmToggleTarget = () => {
    const {
      auth: { user },
      toggleExternalApiTarget,
    } = this.props
    const { pendingToggleRecord, pagination } = this.state

    if (!pendingToggleRecord) {
      return
    }

    toggleExternalApiTarget(user.dsToken, pendingToggleRecord.id, () =>
      this.setState({ pendingToggleRecord: null }, () => this.handleFetchExternalApis(pagination.page))
    )
  }

  handleSave = (record) => {
    const {
      auth: { user },
      updateExternalApi,
      translate: t,
    } = this.props

    updateExternalApi(user.dsToken, record, () => {
      this.setState({
        selectedRecord: null,
        modalMode: 'view',
        successMessage: t('externalApisManagement.edit.success'),
      })
      this.handleFetchExternalApis(this.state.pagination.page)
    })
  }

  setOptionSize = (size) => {
    this.setState(
      {
        optionSize: size,
        pagination: { page: 0, size },
      },
      () => this.handleFetchExternalApis(0)
    )
  }

  setPagination = (newPagination) => {
    this.setState({ pagination: newPagination }, () =>
      this.handleFetchExternalApis(this.state.pagination.page)
    )
  }

  countItemsLabel = () => {
    const { count = 0 } = this.props
    const { pagination } = this.state

    if (!count) return '0 - 0'

    const startItem = pagination.page * pagination.size + 1
    const endItem = Math.min((pagination.page + 1) * pagination.size, count)
    return `${startItem} - ${endItem}`
  }

  render() {
    const { translate: t, records, count = 0, pages = 0, isUpdating, togglingIds = [] } = this.props
    const {
      searchText,
      optionSize,
      pagination,
      selectedRecord,
      modalMode,
      pendingToggleRecord,
      successMessage,
    } = this.state
    const nextTarget =
      pendingToggleRecord && pendingToggleRecord.activeTarget === 'PRD' ? 'DEV' : 'PRD'

    const DropdownIndicator = (props) => (
      <components.DropdownIndicator {...props}>
        <DropdownArrow />
      </components.DropdownIndicator>
    )

    return (
      <div className='external-apis-management-container'>
        <ContentHeader>{t('externalApisManagement.title')}</ContentHeader>

        <div className='padding-bottom header-mission-block external-apis-management-header'>
          <form className='external-apis-management-form' onSubmit={this.handleSearchSubmit}>
            <div className='external-apis-management-search-wrapper'>
              <input
                value={searchText}
                onChange={(event) => this.setState({ searchText: event.target.value })}
                className='external-apis-management-search-input'
                placeholder={t('externalApisManagement.search.placeholder')}
              />

              <button
                type='submit'
                className='external-apis-management-search-button'
                aria-label={t('search_button_label')}
              >
                <img src={MagnifierAlt} className='external-apis-management-search-icon' />
              </button>

              {searchText && (
                <ClearSVG
                  className='external-apis-management-search-clear'
                  onClick={this.handleClearSearch}
                />
              )}
            </div>

            <div className='external-apis-management-target-filter'>
              <Select
                placeholder={t('externalApisManagement.filter.targetPlaceholder')}
                fill
                isMulti
                required
                options={TARGET_FILTER_OPTIONS}
                groupOptions
                onChange={this.handleTargetFilterChange}
                value={this.state.targetFilter}
                showHelperArea={false}
              />
            </div>
          </form>
        </div>

        <div className='external-apis-management-table-section'>
          {count ? (
            <ExternalApisTable
              items={records}
              translate={t}
              onView={(record) => this.setState({ selectedRecord: record, modalMode: 'view' })}
              onEdit={(record) => this.setState({ selectedRecord: record, modalMode: 'edit' })}
              onToggleTarget={this.handleToggleTarget}
              togglingIds={togglingIds}
            >
              <Row className='new-relatory-row stock-transfer-pagination'>
                <Col className='align-center margin-bottom'>
                  <NewPagination
                    navClass='pagination new-relatory'
                    count={count}
                    current={pagination.page + 1}
                    getPage={(newPage) =>
                      this.setPagination({ page: newPage - 1, size: optionSize })
                    }
                    pages={pages || 1}
                  />

                  <div className='datasheet-manager-pagination-size-selector show-pages-new-relatory'>
                    <Col className='show-per-page-text' xs={7}>
                      {t('pagination.size.selector')}
                    </Col>
                    <Col xs={4}>
                      <SizeSelect
                        components={{ DropdownIndicator }}
                        isSearchable={false}
                        styles={colourStyles}
                        classNamePrefix='react-select'
                        onChange={(option) => this.setOptionSize(option.value)}
                        options={PAGE_SIZE_OPTIONS}
                        value={PAGE_SIZE_OPTIONS.find((item) => item.value === optionSize)}
                        menuPlacement='top'
                      />
                    </Col>
                  </div>

                  <div
                    style={{ float: 'left' }}
                    className='datasheet-manager-pagination-size-selector show-items-new-relatory'
                  >
                    <Col>{`${t('pagination.size.show')} ${this.countItemsLabel()} ${t(
                      'pagination.size.of'
                    )} ${count} ${t('pagination.size.items')}`}</Col>
                  </div>
                </Col>
              </Row>
            </ExternalApisTable>
          ) : (
            <div className='relatory-no-data pictogram-empty external-apis-management-empty'>
              <p>{t('externalApisManagement.empty')}</p>
            </div>
          )}
        </div>

        {selectedRecord && (
          <ExternalApiEditModal
            isOpen={Boolean(selectedRecord)}
            record={selectedRecord}
            isUpdating={Boolean(isUpdating)}
            readOnly={modalMode === 'view'}
            translate={t}
            onClose={() => this.setState({ selectedRecord: null, modalMode: 'view' })}
            onSave={this.handleSave}
            onEdit={() => this.setState({ modalMode: 'edit' })}
          />
        )}

        {pendingToggleRecord && (
          <Modal
            isOpen
            size='sm'
            center
            showHeader={false}
            showFooter={false}
            onCloseModal={() => this.setState({ pendingToggleRecord: null })}
            renderChildrenWithProps={({ onClose }) => (
              <div className='external-apis-warning-modal'>
                <i className='glyph glyph-exclamation-mark external-apis-warning-modal-icon' />
                <p className='external-apis-warning-modal-title'>
                  {t('externalApisManagement.toggleConfirm.title')}
                </p>
                <div className='external-apis-warning-modal-content'>
                  <p className='external-apis-warning-modal-message'>
                    {t('externalApisManagement.toggleConfirm.message')}
                  </p>
                  <p className='external-apis-warning-modal-detail'>
                    <strong>{t('externalApisManagement.table.name')}:</strong>
                    <span>{pendingToggleRecord.name}</span>
                  </p>
                  <p className='external-apis-warning-modal-detail'>
                    <strong>{t('externalApisManagement.toggleConfirm.targetLabel')}:</strong>
                    <span>{nextTarget}</span>
                  </p>
                </div>
                <div className='button-row'>
                  <Button
                    className='btn custom-modal-close-button'
                    text={t('close_button_label')}
                    onClick={onClose}
                  />
                  <Button
                    className='btn external-apis-warning-modal-confirm-btn'
                    text={t('confirm_button_label')}
                    onClick={() => {
                      this.confirmToggleTarget()
                      onClose()
                    }}
                  />
                </div>
              </div>
            )}
          />
        )}

        {successMessage && (
          <SuccessModal
            center
            t={t}
            msg={successMessage}
            size='sm'
            handleClose={() => this.setState({ successMessage: '' })}
            fillButton
            buttonText={t('close_button_label')}
          />
        )}
      </div>
    )
  }
}

const mapStateToProps = ({ externalApisReducer, paginateReducer, auth }) => ({
  records: externalApisReducer.list.records,
  filter: externalApisReducer.list.filter,
  isUpdating: externalApisReducer.edit.isUpdating,
  togglingIds: externalApisReducer.togglingIds,
  count: paginateReducer.count,
  pages: paginateReducer.pages,
  current: paginateReducer.current,
  auth,
})

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...externalApisActions }, dispatch)

const ExternalApisManagementTranslated = translate(null, { wait: true })(ExternalApisManagement)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExternalApisManagementTranslated)

export { ExternalApisManagement as ExternalApisManagementContainer }
